import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Activity, Mail, Lock, User, Eye, EyeOff, Globe, MapPin, ArrowLeft } from 'lucide-react';
import Seo from '../components/Seo';

// Shared country configuration - SI vs US units
const BASE_COUNTRIES = [
  { code: 'AU', name: 'Australia', flag: '🇦🇺', haemoglobin: 'g/L', unitSystem: 'SI' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', haemoglobin: 'g/L', unitSystem: 'SI' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', haemoglobin: 'g/L', unitSystem: 'SI' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', haemoglobin: 'g/L', unitSystem: 'SI' },
  { code: 'US', name: 'United States', flag: '🇺🇸', haemoglobin: 'g/dL', unitSystem: 'US' },
  { code: 'OTHER', name: 'Other / Not sure', flag: '🌍', haemoglobin: 'g/L', unitSystem: 'SI' }
];

// Helper to reorder countries with selected country first
const getOrderedCountries = (selectedCode) => {
  if (!selectedCode) return BASE_COUNTRIES;
  const selected = BASE_COUNTRIES.find(c => c.code === selectedCode);
  if (!selected) return BASE_COUNTRIES;
  return [selected, ...BASE_COUNTRIES.filter(c => c.code !== selectedCode)];
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('AU');
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [detectingLocation, setDetectingLocation] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Determine back destination based on referrer
  const getBackDestination = () => {
    // Check for explicit referrer in query params
    const params = new URLSearchParams(location.search);
    const from = params.get('from');
    if (from) return from;
    
    // Check for state passed via navigation
    if (location.state?.from) return location.state.from;
    
    // Default to homepage
    return '/';
  };

  const backDestination = getBackDestination();
  const backLabel = backDestination === '/pricing' ? 'Back to pricing' : 'Back to website';

  useEffect(() => {
    detectUserCountry();
  }, []);

  const detectUserCountry = async () => {
    try {
      // Try ipinfo.io first (50k requests/month free)
      let response = await fetch('https://ipinfo.io/json');
      let data = await response.json();
      
      let countryCode = data.country;
      let countryName = null;
      
      // If ipinfo.io fails, try ipapi.co as fallback
      if (!countryCode) {
        response = await fetch('https://ipapi.co/json/');
        data = await response.json();
        countryCode = data.country_code;
        countryName = data.country_name;
      }
      
      if (countryCode) {
        const supportedCountry = BASE_COUNTRIES.find(c => c.code === countryCode);
        
        if (supportedCountry) {
          setDetectedCountry(supportedCountry);
          setCountry(countryCode);
        } else {
          // Country detected but not in our supported list, keep default
          setDetectedCountry({ code: countryCode, name: countryName || 'Your location' });
        }
      }
    } catch (error) {
      console.log('Could not detect location, using default');
    } finally {
      setDetectingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(email, password, name, country);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // Scroll to top on page load - immediate
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const selectedCountryInfo = BASE_COUNTRIES.find(c => c.code === country);

  return (
    <div className="min-h-screen bg-background px-4 py-3 sm:py-8 flex flex-col">
      <Seo
        title="Create Your Free Account – MyCBC | 30 Days Full Access"
        description="Join MyCBC free — track CLL/SLL blood results, symptoms and treatments. Full access free for 30 days, no credit card required."
        path="/register"
      />
      {/* Back navigation - always visible */}
      <div className="w-full max-w-md mx-auto mb-2 sm:mb-6">
        <Link 
          to={backDestination}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-primary transition-colors text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </Link>
      </div>

      <div className="w-full max-w-md mx-auto flex-1 sm:flex-none flex flex-col">
        {/* Clickable logo/branding - compact on mobile */}
        <Link to="/" className="block text-center mb-3 sm:mb-8 group">
          <Activity className="h-8 w-8 sm:h-12 sm:w-12 text-blue-600 mx-auto mb-1 sm:mb-4 group-hover:scale-110 transition-transform" />
          <h1 className="text-xl sm:text-3xl font-bold text-slate-900">Create Account</h1>
          <p className="hidden sm:block text-slate-600 mt-2">Start tracking your health journey today</p>
          <p className="text-xs sm:text-sm font-semibold text-teal-600 mt-1">
            Your first 30 days unlock the whole site free — after that you keep the free version unless you upgrade
          </p>
          <p className="hidden sm:block text-xs text-slate-400 mt-2 group-hover:text-primary transition-colors">
            Blood Cancer Companion
          </p>
        </Link>
        
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1 sm:mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                <input
                  id="name"
                  type="text"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full h-10 sm:h-12 pl-10 sm:pl-12 pr-4 rounded-xl border border-slate-200 bg-white text-base sm:text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400"
                  placeholder="John Doe"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1 sm:mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                <input
                  id="email"
                  type="email"
                  data-testid="register-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full h-10 sm:h-12 pl-10 sm:pl-12 pr-4 rounded-xl border border-slate-200 bg-white text-base sm:text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1 sm:mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="register-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full h-10 sm:h-12 pl-10 sm:pl-12 pr-10 sm:pr-12 rounded-xl border border-slate-200 bg-white text-base sm:text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400"
                  placeholder="At least 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Eye className="h-4 w-4 sm:h-5 sm:w-5" />}
                </button>
              </div>
            </div>
            
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-slate-700 mb-1 sm:mb-2">
                Country / Region
              </label>
              {detectingLocation && (
                <div className="flex items-center gap-2 mb-1 text-xs text-slate-500">
                  <MapPin className="h-3 w-3 animate-pulse" />
                  <span>Detecting location...</span>
                </div>
              )}
              {detectedCountry && !detectingLocation && (
                <div className="mb-1.5 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs sm:text-sm text-blue-800">
                    <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 inline mr-1" />
                    Detected: {detectedCountry.name}. You can change this below if needed.
                  </p>
                </div>
              )}
              <div className="relative">
                <Globe className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                <select
                  id="country"
                  data-testid="register-country-select"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full h-10 sm:h-12 pl-10 sm:pl-12 pr-4 rounded-xl border border-slate-200 bg-white text-base sm:text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all appearance-none cursor-pointer"
                >
                  {getOrderedCountries(country).map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-[11px] sm:text-xs text-slate-500 mt-1.5 sm:mt-2">
                ℹ️ Your country affects units and reference ranges (e.g., haemoglobin in {selectedCountryInfo?.haemoglobin})
              </p>
            </div>
            
            <button
              type="submit"
              data-testid="register-submit-button"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
          
          <div className="mt-3 sm:mt-6 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;